import React, { useMemo, useRef, useState, useCallback, useEffect, ReactNode } from 'react';
import { Editor, Transforms, Range, createEditor, Descendant, Text } from 'slate';
import { Slate, Editable, ReactEditor, withReact, useSelected, useFocused } from 'slate-react';

import { nonVerbal, NEW_UTTERANCE_TEXT } from './conf';
import ReactDOM from 'react-dom';
import { withHistory } from 'slate-history';
export const Portal = ({ children }: { children?: ReactNode }) => {
    return typeof document === 'object' ? ReactDOM.createPortal(children, document.body) : null;
};
interface Props {
    setEditedContent: (e: string) => void;
    segment: any;
}

const serialize = (node: any) => {
    if (Array.isArray(node)) {
        return node.map((nd: any): any => serialize(nd)).join('');
    }

    if (Text.isText(node)) {
        let string = node.text;

        return string;
    }

    const children = node.children.map((nd: any) => serialize(nd)).join('');

    switch (node.type) {
        case NONVERBALTYPE:
            return node.tag;
        default:
            return children;
    }
};

const NONVERBALTYPE = 'nonverbal';

function deserialize(text: string): Descendant[] {
    const desc: { type: string; children: Descendant[] } = {
        type: 'paragraph',
        children: [],
    };

    let previ = 0;
    let i = 0;
    while (i < text.length) {
        let flag = false;
        if (text[i] === '<') {
            for (const tag of nonVerbal) {
                if (text.slice(i, i + tag.length) === tag) {
                    if (text.slice(previ, i) !== '') {
                        desc.children.push({
                            text: text.slice(previ, i),
                        });
                    }

                    desc.children.push({
                        type: NONVERBALTYPE,
                        tag: tag,
                        children: [{ text: '' }],
                    });
                    i = i + tag.length;
                    previ = i;
                    flag = true;
                    break;
                }
            }
        }

        if (!flag) {
            i++;
        }
    }

    if (previ != i) {
        if (text.slice(previ, i) !== '') {
            desc.children.push({
                text: text.slice(previ, i),
            });
        }
    } else {
        desc.children.push({
            text: ' ',
        });
    }

    return [desc];
}

function TransciptUtteranceEditor(props: Props) {
    const ref = useRef<HTMLDivElement | null >(null);
    const [target, setTarget] = useState<Range | undefined | null>();
    const [index, setIndex] = useState(0);
    const [search, setSearch] = useState('');
    const renderElement = useCallback((props) => <Element {...props} />, []);
    const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

    const { setEditedContent, segment } = props;
    const editor = useMemo(() => withNonVerbal(withReact(withHistory(createEditor()))), [segment]) as ReactEditor;

    const searchRes = nonVerbal.filter((c) => c.toLowerCase().startsWith(search.toLowerCase())).slice(0, 10);


    useEffect(() => {
      const editorEl = document.querySelector('.cvat-slate-trascript-segment-editor');
      editorEl?.focus();
      Transforms.select(editor, { offset: 0, path: [0, 0] });
    }, []);

    const onKeyDown = useCallback(
        (event) => {
            if (target && searchRes.length > 0) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        const prevIndex = index >= searchRes.length - 1 ? 0 : index + 1;
                        setIndex(prevIndex);
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        const nextIndex = index <= 0 ? searchRes.length - 1 : index - 1;
                        setIndex(nextIndex);
                        break;
                    case 'Tab':
                    case 'Enter':
                        event.preventDefault();
                        Transforms.select(editor, target);
                        insertNonVerbal(editor, searchRes[index]);
                        setTarget(null);
                        break;
                    case 'Escape':
                        event.preventDefault();
                        setTarget(null);
                        break;
                }
            }
        },
        [searchRes, editor, index, target],
    );

    useEffect(() => {
        if (target && searchRes.length > 0) {
            const el = ref.current;
            if (el) {
                const domRange = ReactEditor.toDOMRange(editor, target);
                const rect = domRange.getBoundingClientRect();
                el.style.top = `${rect.top + window.scrollY + 24}px`;
                el.style.left = `${rect.left + window.scrollX - (rect.left + window.scrollX > window.innerWidth - 200 ? 200 : 0)}px`;
            }
        }
    }, [searchRes.length, editor, index, search, target]);

    const deserialized = deserialize(segment.text);
    return (
        <Slate
            editor={editor}
            initialValue={deserialized}
            onChange={(value) => {
                const { selection } = editor;

                if (selection && Range.isCollapsed(selection)) {
                    const [start] = Range.edges(selection);

                    // Check if "<" is the first character typed in an empty utterance
                    if (start.offset === 1) {
                        const beforeRange = Editor.range(editor, { path: [0, 0], offset: 0 }, start);
                        const beforeText = Editor.string(editor, beforeRange);
                        if (beforeText === '<') {
                            setTarget(beforeRange);
                            setSearch('<');
                            setIndex(0);
                            return;
                        }
                    }

                    const wordBefore = Editor.before(editor, start, { unit: 'word' });
                    const before = wordBefore && Editor.before(editor, wordBefore);
                    const beforeRange = before && Editor.range(editor, before, start);
                    const beforeText = beforeRange && Editor.string(editor, beforeRange);
                    const beforeMatch = beforeText && beforeText.match(/(<[A-Z]*)$/);
                    const after = Editor.after(editor, start);
                    const afterRange = Editor.range(editor, start, after);
                    const afterText = Editor.string(editor, afterRange);
                    const afterMatch = afterText.match(/^(\s|$)/);

                    if (beforeMatch && afterMatch) {
                        setTarget(beforeRange);
                        setSearch(beforeMatch[1]);
                        setIndex(0);
                        return;
                    }
                }

                setTarget(null);
                setEditedContent(serialize(value));
            }}
        >
            <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                onKeyDown={onKeyDown}
                autoFocus
                className='cvat-slate-trascript-segment-editor'
                placeholder={NEW_UTTERANCE_TEXT}
                style={{height: '100%'}}
            />
            {target && searchRes.length > 0 && (
                <Portal>
                    <div
                        ref={ref}
                        style={{
                            top: '-9999px',
                            left: '-9999px',
                            position: 'absolute',
                            zIndex: 1,
                            padding: '3px',
                            background: 'white',
                            borderRadius: '4px',
                            boxShadow: '0 1px 5px rgba(0,0,0,.2)',
                        }}
                        data-cy='mentions-portal'
                    >
                        {searchRes.map((char, i) => (
                            <div
                                key={char}
                                onClick={() => {
                                    Transforms.select(editor, target);
                                    insertNonVerbal(editor, char);
                                    setTarget(null);
                                }}
                                style={{
                                    padding: '1px 3px',
                                    borderRadius: '3px',
                                    background: i === index ? '#B4D5FF' : 'transparent',
                                }}
                            >
                                {char}
                            </div>
                        ))}
                    </div>
                </Portal>
            )}
        </Slate>
    );
}

const withNonVerbal = (editor: ReactEditor) => {
    const { isInline, isVoid, markableVoid } = editor;

    editor.isInline = (element) => {
        return element.type === NONVERBALTYPE ? true : isInline(element);
    };

    editor.isVoid = (element) => {
        return element.type === NONVERBALTYPE ? true : isVoid(element);
    };

    editor.markableVoid = (element) => {
        return element.type === NONVERBALTYPE || markableVoid(element);
    };

    return editor;
};

const insertNonVerbal = (editor: Editor, nonVerbalTag: string) => {
    const nonverbal = {
        type: NONVERBALTYPE,
        tag: nonVerbalTag,
        children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, nonverbal);
    Transforms.move(editor);
};

const Element = (props: any) => {
    const { attributes, children, element } = props;
    switch (element.type) {
        case NONVERBALTYPE:
            return <NonVerbal tag={element.tag} children={children} attributes={attributes} />;
        default:
            return <p {...attributes}>{children}</p>;
    }
};

const Leaf = ({ attributes, children }: any) => {
    return <span {...attributes}>{children}</span>;
};

const NonVerbal = ({ tag, children, attributes }: { tag: string; children: any[]; attributes: any }) => {
    const selected = useSelected();
    const focused = useFocused();
    const style: React.CSSProperties = {
        padding: '3px 3px 2px',
        margin: '0 1px',
        verticalAlign: 'baseline',
        display: 'inline-block',
        borderRadius: '4px',
        backgroundColor: '#800',
        fontSize: '0.9em',
        boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none',
    };

    return (
        <span {...attributes} data-cy={tag} style={style} contentEditable={false}>
            {tag}
            {children}
        </span>
    );
};

export default TransciptUtteranceEditor;
